// ============================================
// COMPONENT - Modal Detalle Fiado
// ============================================
// Modal para visualizar detalle de un fiado con productos comprados

import {
  MdClose,
  MdReceipt,
  MdPerson,
  MdPhone,
  MdCalendarToday,
  MdShoppingCart,
  MdCheckCircle,
  MdCancel,
  MdHistory,
  MdWarning,
} from 'react-icons/md';
import { GiWallet } from 'react-icons/gi';
import { FaMoneyBillWave, FaCreditCard, FaExchangeAlt, FaHandHolding } from 'react-icons/fa';
import { SiVisa } from 'react-icons/si';
import { useBoletaDetalle, useAbonosFiado, TipoPago } from '@almacenes/core';
import type { FiadoHistorico, BoletaPago } from '@almacenes/core';
import { capitalize, formatPhoneNumber } from '@almacenes/ui';

interface ModalDetalleFiadoProps {
  fiado: FiadoHistorico;
  onClose: () => void;
}

const getPaymentIcon = (methodPayment: string) => {
  switch (methodPayment) {
    case TipoPago.EFECTIVO:
      return <FaMoneyBillWave className="text-lg text-green-600" />;
    case TipoPago.DEBITO:
      return <FaCreditCard className="text-lg text-blue-600" />;
    case TipoPago.CREDITO:
      return <SiVisa className="text-lg text-purple-600" />;
    case TipoPago.TRANSFERENCIA:
      return <FaExchangeAlt className="text-lg text-orange-600" />;
    case TipoPago.FIADO:
      return <FaHandHolding className="text-lg text-red-600" />;
    default:
      return <FaMoneyBillWave className="text-lg text-gray-600" />;
  }
};

export const ModalDetalleFiado = ({ fiado, onClose }: ModalDetalleFiadoProps) => {
  const { data: boletaDetalle, isLoading, error } = useBoletaDetalle(fiado.boletaId);
  const { data: abonos, isLoading: isLoadingAbonos } = useAbonosFiado(fiado.fiadoId);

  const pagos: BoletaPago[] = boletaDetalle?.payments ?? [];
  const esVentaMixta = pagos.length > 1;
  const otrosMedios = pagos.filter((p) => p.methodPayment !== TipoPago.FIADO);
  const totalOtrosMedios = otrosMedios.reduce((sum, p) => sum + p.amount, 0);
  const hayDescuadre = !esVentaMixta && boletaDetalle != null && boletaDetalle.total !== fiado.montoTotal;

  const formatearFecha = (fecha: string) => {
    return new Date(fecha).toLocaleString('es-CL', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="bg-gradient-to-r from-[#43BFFF] to-[#2BA5E8] px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-white bg-opacity-20 rounded-full flex items-center justify-center">
              <MdReceipt className="text-2xl text-white" />
            </div>
            <div>
              <h3 className="text-xl font-bold text-white">Detalle del Fiado</h3>
              <p className="text-sm text-blue-100">Boleta #{fiado.boletaId}</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-white hover:bg-white hover:bg-opacity-20 p-2 rounded-lg transition-colors"
          >
            <MdClose className="text-2xl" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {/* Información del Cliente y Fiado */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
            {/* Info Cliente */}
            <div className="bg-gray-50 rounded-lg p-4">
              <h4 className="text-sm font-bold text-gray-600 mb-3 uppercase">Información del Cliente</h4>
              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <MdPerson className="text-gray-400" />
                  <span className="text-sm text-gray-700">
                    <span className="font-medium">Nombre:</span> {capitalize(fiado.aliasNombre || fiado.clienteNombre)}
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <MdPhone className="text-gray-400" />
                  <span className="text-sm text-gray-700">
                    <span className="font-medium">Teléfono:</span> {formatPhoneNumber(fiado.clienteTelefono)}
                  </span>
                </div>
                {fiado.clienteRut && fiado.clienteDv && (
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-gray-700">
                      <span className="font-medium">RUT:</span> {fiado.clienteRut}-{fiado.clienteDv}
                    </span>
                  </div>
                )}
              </div>
            </div>

            {/* Info Fiado */}
            <div className="bg-gray-50 rounded-lg p-4">
              <h4 className="text-sm font-bold text-gray-600 mb-3 uppercase">Información del Fiado</h4>
              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <MdCalendarToday className="text-gray-400" />
                  <span className="text-sm text-gray-700">
                    <span className="font-medium">Fecha:</span> {formatearFecha(fiado.fecha)}
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <MdReceipt className="text-gray-400" />
                  <span className="text-sm text-gray-700">
                    <span className="font-medium">N° Boleta:</span> #{fiado.boletaId}
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  {fiado.pagado ? (
                    <MdCheckCircle className="text-green-600" />
                  ) : (
                    <MdCancel className="text-yellow-600" />
                  )}
                  <span className="text-sm text-gray-700">
                    <span className="font-medium">Estado:</span>{' '}
                    <span className={fiado.pagado ? 'text-green-600 font-medium' : 'text-yellow-600 font-medium'}>
                      {fiado.pagado ? 'Pagado' : 'Pendiente'}
                    </span>
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Resumen Financiero */}
          <div className="bg-blue-50 rounded-lg p-4 mb-6">
            <h4 className="text-sm font-bold text-gray-600 mb-3 uppercase">Resumen Financiero</h4>
            <div className="grid grid-cols-3 gap-4">
              <div>
                <p className="text-xs text-gray-600 mb-1">Monto fiado</p>
                <p className="text-lg font-bold text-gray-900">${fiado.montoTotal.toLocaleString('es-CL')}</p>
              </div>
              <div>
                <p className="text-xs text-gray-600 mb-1">Abonado</p>
                <p className="text-lg font-bold text-green-600">${fiado.montoAbonado.toLocaleString('es-CL')}</p>
              </div>
              <div>
                <p className="text-xs text-gray-600 mb-1">Saldo Pendiente</p>
                <p className="text-lg font-bold text-red-600">${fiado.saldoPendiente.toLocaleString('es-CL')}</p>
              </div>
            </div>
          </div>

          {/* Descuadre entre el monto fiado y el total de la boleta (venta de pago único) */}
          {hayDescuadre && boletaDetalle && (
            <div className="flex items-start gap-2 bg-amber-50 border border-amber-200 rounded-lg p-3 mb-6">
              <MdWarning className="text-lg text-amber-600 flex-shrink-0 mt-0.5" />
              <p className="text-sm text-amber-800">
                El total de la boleta (${boletaDetalle.total.toLocaleString('es-CL')}) no coincide con el monto
                fiado registrado (${fiado.montoTotal.toLocaleString('es-CL')}). Es posible que la boleta se haya
                editado después de la venta.
              </p>
            </div>
          )}

          {/* Distribución del pago (venta mixta: parte de la boleta se pagó con otro medio) */}
          {esVentaMixta && boletaDetalle && (
            <div className="bg-gray-50 rounded-lg border border-gray-200 overflow-hidden mb-6">
              <div className="flex items-center gap-2 px-4 pt-4 pb-2">
                <GiWallet className="text-xl text-purple-600" />
                <h4 className="text-sm font-bold text-gray-600 uppercase">Distribución del Pago</h4>
              </div>
              <p className="text-xs text-gray-500 px-4 pb-3">
                Esta boleta se pagó con más de un medio. Solo la porción FIADO quedó como deuda; el resto ya se
                pagó al momento de la venta.
              </p>
              <div>
                {[...pagos].sort((a, b) => a.orden - b.orden).map((pago, index, arr) => {
                  const esFiado = pago.methodPayment === TipoPago.FIADO;
                  const isLast = index === arr.length - 1;
                  return (
                    <div
                      key={pago.orden}
                      className={`flex items-center gap-3 p-3 ${esFiado ? 'bg-red-50' : ''} ${
                        !isLast ? 'border-b border-gray-200' : ''
                      }`}
                    >
                      <div className="flex-shrink-0">{getPaymentIcon(pago.methodPayment)}</div>
                      <div className="flex-1 text-sm font-medium text-gray-700">{pago.methodPayment}</div>
                      <span
                        className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                          esFiado ? 'bg-red-100 text-red-700' : 'bg-green-100 text-green-700'
                        }`}
                      >
                        {esFiado ? 'Tu deuda' : 'Ya pagado'}
                      </span>
                      <span className={`font-bold ${esFiado ? 'text-red-600' : 'text-green-600'}`}>
                        ${pago.amount.toLocaleString('es-CL')}
                      </span>
                    </div>
                  );
                })}
              </div>
              <div className="bg-gray-100 border-t border-gray-200 px-4 py-3 text-sm text-gray-700">
                Total boleta <span className="font-bold">${boletaDetalle.total.toLocaleString('es-CL')}</span>
                {' = '}
                Fiado <span className="font-bold text-red-600">${fiado.montoTotal.toLocaleString('es-CL')}</span>
                {' + '}
                Otros medios <span className="font-bold text-green-600">${totalOtrosMedios.toLocaleString('es-CL')}</span>
              </div>
            </div>
          )}

          {/* Historial de Abonos */}
          <div className="mb-6">
            <div className="flex items-center gap-2 mb-3">
              <MdHistory className="text-green-600 text-xl" />
              <h4 className="text-sm font-bold text-gray-600 uppercase">Historial de Abonos</h4>
            </div>
            {isLoadingAbonos ? (
              <div className="flex justify-center py-4">
                <div className="w-6 h-6 border-4 border-[#43BFFF] border-t-transparent rounded-full animate-spin" />
              </div>
            ) : abonos && abonos.length > 0 ? (
              <div className="border border-gray-200 rounded-lg overflow-hidden">
                <table className="w-full">
                  <thead className="bg-gray-100">
                    <tr>
                      <th className="px-4 py-2 text-left text-xs font-bold text-gray-700 uppercase">Fecha</th>
                      <th className="px-4 py-2 text-left text-xs font-bold text-gray-700 uppercase">Tipo</th>
                      <th className="px-4 py-2 text-right text-xs font-bold text-gray-700 uppercase">Monto</th>
                    </tr>
                  </thead>
                  <tbody>
                    {abonos.map((abono, index) => (
                      <tr key={abono.id} className={index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                        <td className="px-4 py-2 text-sm text-gray-900">
                          {new Date(abono.fechaPago + 'T00:00:00').toLocaleDateString('es-CL', {
                            day: '2-digit', month: '2-digit', year: 'numeric',
                          })}
                        </td>
                        <td className="px-4 py-2 text-sm text-gray-700">{abono.tipoPago}</td>
                        <td className="px-4 py-2 text-sm font-bold text-green-600 text-right">
                          ${abono.monto.toLocaleString('es-CL')}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <p className="text-sm text-gray-500 text-center py-3">Sin abonos registrados</p>
            )}
          </div>

          {/* Productos Comprados */}
          <div>
            <div className="flex items-center gap-2 mb-3">
              <MdShoppingCart className="text-[#43BFFF] text-xl" />
              <h4 className="text-sm font-bold text-gray-600 uppercase">Productos Comprados</h4>
            </div>

            {isLoading ? (
              <div className="text-center py-8">
                <div className="flex justify-center mb-3">
                  <div className="w-8 h-8 border-4 border-[#43BFFF] border-t-transparent rounded-full animate-spin" />
                </div>
                <p className="text-gray-600">Cargando productos...</p>
              </div>
            ) : error ? (
              <div className="text-center py-8 text-red-500">
                <p>Error al cargar los productos</p>
                <p className="text-sm text-gray-600 mt-2">Por favor, intente nuevamente</p>
              </div>
            ) : boletaDetalle && boletaDetalle.detalles.length > 0 ? (
              <div className="border border-gray-200 rounded-lg overflow-hidden">
                <table className="w-full">
                  <thead className="bg-gray-100">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-bold text-gray-700 uppercase">Producto</th>
                      <th className="px-4 py-3 text-center text-xs font-bold text-gray-700 uppercase">Cantidad</th>
                      <th className="px-4 py-3 text-right text-xs font-bold text-gray-700 uppercase">Precio Unit.</th>
                      <th className="px-4 py-3 text-right text-xs font-bold text-gray-700 uppercase">Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {boletaDetalle.detalles.map((detalle, index) => (
                      <tr key={index} className={index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-2">
                            <span className="text-sm text-gray-900">{detalle.nombreProducto}</span>
                            {detalle.esPack && (
                              <span className="px-2 py-0.5 bg-purple-100 text-purple-700 text-xs rounded-full font-medium">
                                Pack
                              </span>
                            )}
                          </div>
                        </td>
                        <td className="px-4 py-3 text-center">
                          <span className="text-sm font-medium text-gray-900">{detalle.cantidad}</span>
                        </td>
                        <td className="px-4 py-3 text-right">
                          <span className="text-sm text-gray-900">${detalle.precioVenta.toLocaleString('es-CL')}</span>
                        </td>
                        <td className="px-4 py-3 text-right">
                          <span className="text-sm font-bold text-gray-900">${detalle.total.toLocaleString('es-CL')}</span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot className="bg-gray-100 border-t-2 border-gray-300">
                    <tr>
                      <td colSpan={3} className="px-4 py-3 text-right">
                        <span className="text-sm font-bold text-gray-700">TOTAL BOLETA:</span>
                      </td>
                      <td className="px-4 py-3 text-right">
                        <span className="text-lg font-bold text-[#43BFFF]">
                          ${boletaDetalle.total.toLocaleString('es-CL')}
                        </span>
                      </td>
                    </tr>
                    {esVentaMixta && (
                      <tr>
                        <td colSpan={3} className="px-4 py-2 text-right">
                          <span className="text-xs font-bold text-red-700">DEUDA FIADA:</span>
                        </td>
                        <td className="px-4 py-2 text-right">
                          <span className="text-sm font-bold text-red-600">
                            ${fiado.montoTotal.toLocaleString('es-CL')}
                          </span>
                        </td>
                      </tr>
                    )}
                  </tfoot>
                </table>
              </div>
            ) : (
              <div className="text-center py-8 text-gray-500">
                <p>No se encontraron productos en esta boleta</p>
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="px-6 py-4 bg-gray-50 border-t border-gray-200">
          <button
            onClick={onClose}
            className="w-full px-6 py-2 bg-[#43BFFF] text-white rounded-lg hover:bg-[#2BA5E8] transition-colors font-medium"
          >
            Cerrar
          </button>
        </div>
      </div>
    </div>
  );
};
